import React, { createContext, useContext, useEffect, useState, useCallback } from "react";

interface AccessibilityState {
  prefersReducedMotion: boolean;
  prefersHighContrast: boolean;
  announce: (message: string, priority?: "polite" | "assertive") => void;
}

const AccessibilityContext = createContext<AccessibilityState>({
  prefersReducedMotion: false,
  prefersHighContrast: false,
  announce: () => {},
});

export function useAccessibility() {
  return useContext(AccessibilityContext);
}

interface Props {
  children: React.ReactNode;
}

export function AccessibilityProvider({ children }: Props) {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [prefersHighContrast, setPrefersHighContrast] = useState(false);
  const [announcement, setAnnouncement] = useState({ text: "", priority: "polite" as "polite" | "assertive", key: 0 });

  useEffect(() => {
    const motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const contrastQuery = window.matchMedia("(prefers-contrast: more)");

    setPrefersReducedMotion(motionQuery.matches);
    setPrefersHighContrast(contrastQuery.matches);

    const handleMotionChange = (e: MediaQueryListEvent) => setPrefersReducedMotion(e.matches);
    const handleContrastChange = (e: MediaQueryListEvent) => setPrefersHighContrast(e.matches);

    motionQuery.addEventListener("change", handleMotionChange);
    contrastQuery.addEventListener("change", handleContrastChange);

    return () => {
      motionQuery.removeEventListener("change", handleMotionChange);
      contrastQuery.removeEventListener("change", handleContrastChange);
    };
  }, []);

  const announce = useCallback((text: string, priority: "polite" | "assertive" = "polite") => {
    setAnnouncement((prev) => ({ text, priority, key: prev.key + 1 }));
  }, []);

  return (
    <AccessibilityContext.Provider value={{ prefersReducedMotion, prefersHighContrast, announce }}>
      {children}
      {/* Live region for screen reader announcements */}
      <div
        role="status"
        aria-live={announcement.priority}
        aria-atomic="true"
        className="sr-only"
        key={announcement.key}
      >
        {announcement.text}
      </div>
    </AccessibilityContext.Provider>
  );
}
